import { VipLevel, VipLevelIntl } from './userInfo';

/** 协作者权限 */
export enum CollaboratorPermissionType {
    /** 可查看 */
    Views = 2,
    /** 可编辑 */
    Edit = 3,
    /** 可管理 */
    Manage = 4,
    /** 移除 */
    Remove = 5,
    /** 拒绝 */
    Reject = 6,
    /** 所有者 */
    Owner = 999999,
}

// 身份类型，type: 0-个人版，1-团队版，2-企业版
export enum IdentityType {
    // 0-个人版
    PERSONAL = 0,
    // 1-团队版
    TEAM = 1,
    // 2-企业版
    ENTERPRISE = 2,
}

/**
 * 团队身份
 */
export interface ITeamIdentity {
    /**
     * 是否是ai无限
     */
    ai_unlimited: number;
    oid: number;
    ouid: number;
    corp_id: number;
    name: string;
    /** 别名，如果存在用别名展示 */
    alias: string;
    logo: string;
    create_at: number;
    groups: {
        id: number;
        app_id: number;
        oid: number;
        name: string;
        /** 有效时间 */
        validate_time: number;
        /** 过期时间 */
        invalidate_time: number;
        is_vip: boolean;
        vip_level: VipLevel | VipLevelIntl;
    }[];
    member_num: number;
    /** 0-个人版，1-团队版，2-企业版 */
    type: number;
}

export interface IResponseTeamIdentity {
    /** 身份列表(包含个人身份、团队身份、企业身份) */
    list: ITeamIdentity[];
    /** 该帐号是否购买过团队版会员 */
    had_buy: boolean;
}

export interface IResponseTeamMember {
    /** 角色 */
    role: TeamRoleType;
    /** 我的席位状态 */
    seat_status: TeamSeatStatus;
    /** 我的席位有效时间	 */
    invalidate_time: number;
    /** 是否有席位 */
    has_seat: number;
    team: {
        /** 是否是vip */
        is_vip: boolean;
        vip_end_time: number;
        /** 团队席位状态 */
        member_detail: ITeamMemberDetail;
        /** 该团队是否购买过会员 */
        had_buy: boolean;
    };
    /** 1在职, 2离职 */
    job_status: 1 | 2;
}

/**
 * 团队信息
 */
export interface ITeamInfo {
    /** 身份信息 */
    identity: {
        /** 团队id */
        oid: number;
        /** 权益id */
        groupId: number;
    } & IResponseTeamIdentity;
    /** 成员信息 */
    member?: IResponseTeamMember;
}

/**
 * 团队席位状态
 */
export interface ITeamMemberDetail {
    /** 总席总数 */
    total: number;
    /** 有效的席位数	 */
    valid_num: number;
    /** 失效的席位数	 */
    invalid_num: number;
    /** 冻结的席位数 */
    freeze_num: number;
    /** 生效可操作席位数 */
    operable_num: number;
    /** 生效可操作已绑定席位数 */
    operable_bind_num: number;
    /** 待生效可操作席位 */
    wait_operable_num: number;
    /** 席位冻结时间 */
    earliest_invalidate_time: number;
}

/** 席位状态 */
export enum TeamSeatStatus {
    /** 未绑定 */
    Unbound = 0,
    /** 生效 */
    Effect = 1,
    /** 待生效 */
    Pending = 2,
    /** 冻结(过期) */
    Freeze = 10,
}

/**
 * 团队角色
 */
export enum TeamRoleType {
    /** 所有者 */
    owner = 'owner',
    /** 管理员 */
    admin = 'admin',
    /** 成员 */
    member = 'member',
}

/**
 * 协作列表
 */
export interface Collaborator {
    uid: number;
    name: string;
    avatar: string;
    is_team_member: boolean;
    permission_mode: CollaboratorPermissionType;
    permission_set: string;
    permission_mode_name: string;
}

export interface Asker {
    uid?: number;
    name?: string;
    avatar?: string;
    is_team_member?: boolean;
    ask_permission_id?: number;
    permission_mode?: CollaboratorPermissionType;
    team?: {
        name: string;
    };
}

export interface CollaboratorsData {
    owner: {
        name: string;
        avatar: string;
        uid: number;
    };
    collaborator: Collaborator[] | null;
    askers: Asker[];
}

/**
 * 团队成员
 */
export interface TeamMember {
    avatar: string;
    has_seat: number;
    is_self: boolean;
    job_status: number;
    name: string;
    ouid: number;
    phone: string;
    role: string;
    role_name: string;
    seat: { status: number; status_name: string };
    status: number;
    uid: number;
    checked: boolean;
}

/**
 * 团队信息
 */
export interface TeamInfoResponse {
    oid: number;
    corp_id: string;
    name: string;
    type: number;
    logo: string;
    user_count: number;
    super_admin_name: string;
    industry_name: string;
    created_at: number;
    member_detail: ITeamMemberDetail;
    is_vip: boolean;
    /** 过期天数, 过期3天则为-3 */
    vip_days: number;
    vip_end_time: number;
    accredit: {
        /** 用于判断是否填写了授权书 */
        created_at: number;
    } | null;
}

/**
 * 回调的type
 */
export enum TeamShareChangeType {
    USER_TYPE = 'user_type',
    MODE = 'mode',
}

/**
 * 分享文件类型
 */
export enum TeamShareNodeType {
    RECORD = 'RECORD',
    TEMPLATE = 'TEMPLATE',
    FOLDER = 'FOLDER',
}

export interface IRequestTeamCreate {
    name: string;
    rebate_code?: string;
}

/**
 * 创建团队
 */
export interface IResponseTeamCreate {
    group_id: number;
    oid: number;
    ouid: number;
    team_name: string;
}

/**
 * [团队]生成邀请码
 */
export interface IResponseTeamInvite {
    id: number;
    invite_key: string;
    shortUrl: string;
    ext: string;
    seat: {
        remaing: number;
    };
}
export interface IRequestTeamInvite {
    id?: number;
    expired_days?: number;
    role?: string;
}

export interface DownloadRecordRequest {
    // 一级菜单： AI商拍   图像处理  AI设计
    menu?: string;
    // 二级菜单
    sub_menu?: string;
    // 下载文件的网络地址
    url?: string;
    // 文件的扩展名  jpg png zip pptx
    extension?: string;
    // 文件id，用于去重，有则填写
    mt_image_id?: string;
    width?: number;
    height?: number;
    filesize?: number;
}

export type IOperatePermissionKey =
    | 'team.info'
    | 'team.update'
    | 'team.contact'
    | 'team.pay'
    | 'team.unregister'
    | 'member.list'
    | 'member.invite'
    | 'member.change_role'
    | 'member.remove'
    | 'order.list'
    | 'order.make_invoice'
    | 'data.summary'
    | 'accredit.list'
    | 'accredit.create';
